import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Users, 
  UserCheck, 
  UserX, 
  Shield, 
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  RefreshCw,
  Mail,
  Calendar
} from "lucide-react";
import { toast } from "sonner";

export default function UserManagement() {
  const { isAdmin, getPendingUsers, approveUser, rejectUser, getAllUsers } = useAuth();
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [activeTab, setActiveTab] = useState("pending");

  useEffect(() => {
    if (isAdmin()) {
      fetchData();
    }
  }, [isAdmin]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pending, all] = await Promise.all([
        getPendingUsers(),
        getAllUsers()
      ]);
      setPendingUsers(pending.users || []);
      setAllUsers(all || []);
    } catch (error) {
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId, userName) => {
    setActionLoading(userId);
    try {
      await approveUser(userId);
      toast.success(`${userName} has been approved`);
      fetchData();
    } catch (error) {
      toast.error("Failed to approve user");
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (userId, userName) => {
    setActionLoading(userId);
    try {
      await rejectUser(userId);
      toast.success(`${userName} has been rejected`);
      fetchData();
    } catch (error) {
      toast.error("Failed to reject user");
    } finally {
      setActionLoading(null);
    }
  };

  if (!isAdmin()) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Shield className="w-16 h-16 text-[#e76f51] mx-auto mb-4" />
          <h2 className="text-xl text-[#faf3e0] font-bold mb-2">Access Denied</h2>
          <p className="text-[#a8c8c4]">You need admin privileges to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div 
            className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#e9c46a] to-[#f4a261] flex items-center justify-center"
            style={{ boxShadow: '4px 4px 0 rgba(139, 69, 19, 0.3)' }}
          >
            <Users className="w-7 h-7 text-[#264653]" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-[#faf3e0]">User Management</h1>
            <p className="text-[#a8c8c4] font-mono text-sm">APPROVE AND MANAGE USERS</p>
          </div>
        </div>
        <Button
          onClick={fetchData}
          disabled={loading}
          variant="outline"
          className="border-2 border-[#2a9d8f]/50 text-[#40c9ba] hover:bg-[#2a9d8f]/20"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 rounded-xl bg-[#1a3a42]/80 border-2 border-[#e9c46a]/30">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#e9c46a]/20 flex items-center justify-center">
              <Clock className="w-5 h-5 text-[#e9c46a]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#e9c46a]">{pendingUsers.length}</p>
              <p className="text-xs text-[#a8c8c4] font-mono">PENDING APPROVAL</p>
            </div>
          </div>
        </div>
        <div className="p-4 rounded-xl bg-[#1a3a42]/80 border-2 border-[#2a9d8f]/30">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#2a9d8f]/20 flex items-center justify-center">
              <UserCheck className="w-5 h-5 text-[#2a9d8f]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#2a9d8f]">{allUsers.filter(u => u.approved).length}</p>
              <p className="text-xs text-[#a8c8c4] font-mono">APPROVED USERS</p>
            </div>
          </div>
        </div>
        <div className="p-4 rounded-xl bg-[#1a3a42]/80 border-2 border-[#f4a261]/30">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#f4a261]/20 flex items-center justify-center">
              <Shield className="w-5 h-5 text-[#f4a261]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#f4a261]">{allUsers.filter(u => u.role === 'admin').length}</p>
              <p className="text-xs text-[#a8c8c4] font-mono">ADMINS</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b-2 border-[#2a9d8f]/30 pb-2">
        <button
          onClick={() => setActiveTab("pending")}
          className={`px-4 py-2 rounded-t-lg font-medium transition-all ${
            activeTab === "pending"
              ? "bg-[#e9c46a] text-[#264653]"
              : "text-[#a8c8c4] hover:text-[#faf3e0]"
          }`}
        >
          Pending ({pendingUsers.length})
        </button>
        <button
          onClick={() => setActiveTab("all")}
          className={`px-4 py-2 rounded-t-lg font-medium transition-all ${
            activeTab === "all"
              ? "bg-[#2a9d8f] text-white"
              : "text-[#a8c8c4] hover:text-[#faf3e0]"
          }`}
        >
          All Users ({allUsers.length})
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#e9c46a]" />
        </div>
      ) : activeTab === "pending" ? (
        <div className="space-y-3">
          {pendingUsers.length === 0 ? (
            <div className="text-center py-12">
              <UserCheck className="w-16 h-16 text-[#2a9d8f]/50 mx-auto mb-4" />
              <p className="text-[#a8c8c4]">No pending approvals</p>
            </div>
          ) : (
            pendingUsers.map((user) => (
              <div
                key={user.user_id}
                className="p-4 rounded-xl bg-[#1a3a42]/80 border-2 border-[#e9c46a]/40 hover:border-[#e9c46a]/60 transition-all"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="w-12 h-12 rounded-lg object-cover" />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#2a9d8f] to-[#1a6b62] flex items-center justify-center">
                        <span className="text-lg font-bold text-white">{user.name?.charAt(0)?.toUpperCase()}</span>
                      </div>
                    )}
                    <div>
                      <p className="text-[#faf3e0] font-semibold">{user.name}</p>
                      <div className="flex items-center gap-2 text-sm text-[#a8c8c4]">
                        <Mail className="w-3 h-3" />
                        {user.email}
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        <Badge variant="outline" className="text-xs border-[#f4a261]/50 text-[#f4a261]">
                          {user.auth_provider === 'google' ? 'Google' : 'Email'}
                        </Badge>
                        <span className="text-xs text-[#a8c8c4] flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(user.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleApprove(user.user_id, user.name)}
                      disabled={actionLoading === user.user_id}
                      className="bg-[#2a9d8f] hover:bg-[#40c9ba] text-white"
                    >
                      {actionLoading === user.user_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Approve
                        </>
                      )}
                    </Button>
                    <Button
                      onClick={() => handleReject(user.user_id, user.name)}
                      disabled={actionLoading === user.user_id}
                      variant="outline"
                      className="border-[#e76f51] text-[#e76f51] hover:bg-[#e76f51]/20"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Reject
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {allUsers.map((user) => (
            <div
              key={user.user_id}
              className={`p-4 rounded-xl bg-[#1a3a42]/80 border-2 transition-all ${
                user.approved ? 'border-[#2a9d8f]/30' : 'border-[#e9c46a]/40'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-lg object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#2a9d8f] to-[#1a6b62] flex items-center justify-center">
                      <span className="text-sm font-bold text-white">{user.name?.charAt(0)?.toUpperCase()}</span>
                    </div>
                  )}
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-[#faf3e0] font-medium">{user.name}</p>
                      {user.role === 'admin' && (
                        <Badge className="bg-[#f4a261]/20 text-[#f4a261] border-[#f4a261]/40 text-xs">
                          <Shield className="w-3 h-3 mr-1" />
                          Admin
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-[#a8c8c4]">{user.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className={`text-xs ${
                    user.approved 
                      ? 'border-[#2a9d8f]/50 text-[#2a9d8f]' 
                      : 'border-[#e9c46a]/50 text-[#e9c46a]'
                  }`}>
                    {user.approved ? 'Approved' : 'Pending'}
                  </Badge>
                  <Badge variant="outline" className="text-xs border-[#a8c8c4]/30 text-[#a8c8c4]">
                    {user.auth_provider === 'google' ? 'Google' : 'Email'}
                  </Badge>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
